import React, { useState, useEffect, useMemo } from 'react';
import { getFloorPlan } from '../../../../services/FloorPlanService';

const MINI_CELL = 18;

const FloorPlanMiniMap = ({ eventId, vendorUserId }) => {
  const [plan, setPlan] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!eventId) return;
    const load = async () => {
      setIsLoading(true);
      try {
        const data = await getFloorPlan(eventId);
        setPlan(data);
      } catch (err) {
        console.error('Failed to load mini-map:', err);
      } finally {
        setIsLoading(false);
      }
    };
    load();
  }, [eventId]);

  const vendorBooth = useMemo(() => {
    if (!plan || !vendorUserId) return null;
    return plan.booths?.find(b => b.vendorId === vendorUserId) || null;
  }, [plan, vendorUserId]);

  const pathwaySet = useMemo(() =>
    new Set(plan?.pathwayCells || []),
    [plan]
  );

  // Build cell occupancy map for mini-map
  const cellMap = useMemo(() => {
    if (!plan) return {};
    const map = {};
    for (const booth of (plan.booths || [])) {
      for (let r = booth.row; r < booth.row + booth.rowSpan; r++) {
        for (let c = booth.col; c < booth.col + booth.colSpan; c++) {
          map[`${r},${c}`] = { type: 'booth', booth };
        }
      }
    }
    for (const key of pathwaySet) {
      if (!map[key]) map[key] = { type: 'pathway' };
    }
    return map;
  }, [plan, pathwaySet]);

  if (isLoading) {
    return (
      <div className="h-24 flex items-center justify-center">
        <p className="text-xs text-grey-400">Loading map...</p>
      </div>
    );
  }

  if (!plan || !plan.booths?.length) return null;

  const { gridWidth, gridHeight } = plan;

  return (
    <div className="bg-white rounded-xl border border-grey-200 p-4">
      <div className="flex items-center justify-between mb-3">
        <p className="text-sm font-semibold text-grey-900">Venue Map</p>
        {vendorBooth ? (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-primary-50 text-primary-700 rounded-full text-xs font-semibold">
            <span className="w-1.5 h-1.5 rounded-full bg-primary-600" />
            Booth #{vendorBooth.number}
          </span>
        ) : (
          <span className="text-xs text-grey-400">Not yet placed</span>
        )}
      </div>

      <div className="overflow-auto">
        <div
          style={{
            position: 'relative',
            width:    gridWidth  * MINI_CELL,
            height:   gridHeight * MINI_CELL,
          }}
        >
          {/* Background cells */}
          {Array.from({ length: gridHeight }, (_, r) =>
            Array.from({ length: gridWidth }, (_, c) => {
              const key = `${r},${c}`;
              const cell = cellMap[key];
              let bg = '#FFFFFF';
              let border = '#E5E7EB';
              if (cell?.type === 'pathway') { bg = '#F3F4F6'; border = '#D1D5DB'; }
              return (
                <div
                  key={key}
                  style={{
                    position:  'absolute',
                    left:      c * MINI_CELL,
                    top:       r * MINI_CELL,
                    width:     MINI_CELL,
                    height:    MINI_CELL,
                    background: bg,
                    border:    `1px solid ${border}`,
                    boxSizing: 'border-box',
                  }}
                />
              );
            })
          )}

          {/* Booths */}
          {(plan.booths || []).map(booth => {
            const isVendorBooth = vendorBooth?.id === booth.id;
            return (
              <div
                key={booth.id}
                style={{
                  position:        'absolute',
                  left:            booth.col * MINI_CELL + 2,
                  top:             booth.row * MINI_CELL + 2,
                  width:           booth.colSpan * MINI_CELL - 4,
                  height:          booth.rowSpan * MINI_CELL - 4,
                  backgroundColor: isVendorBooth ? '#B84141' : booth.vendorId ? '#E5E7EB' : '#F9FAFB',
                  border:          `1.5px solid ${isVendorBooth ? '#9B2C2C' : '#CBD5E1'}`,
                  borderRadius:    3,
                  zIndex:          10,
                  display:         'flex',
                  alignItems:      'center',
                  justifyContent:  'center',
                }}
              >
                {isVendorBooth && (
                  <span style={{ fontSize: 7, fontWeight: 800, color: '#FFFFFF', lineHeight: 1 }}>
                    YOU
                  </span>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {vendorBooth ? (
        <p className="mt-3 text-xs text-grey-500 text-center">
          Your booth is highlighted in red
        </p>
      ) : (
        <p className="mt-3 text-xs text-grey-400 text-center">
          Your organizer hasn't placed your booth yet
        </p>
      )}
    </div>
  );
};

export default FloorPlanMiniMap;
